import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { 
  Search, 
  Plus, 
  X, 
  Loader2, 
  Filter,
  Database,
  ChevronDown,
  ChevronUp,
  Trash2,
  Download,
  RefreshCw
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

// Operators for different field types
const OPERATORS = {
  text: [
    { value: 'contains', label: 'Содержит' },
    { value: 'equals', label: 'Равно' },
    { value: 'starts_with', label: 'Начинается с' },
    { value: 'ends_with', label: 'Заканчивается на' },
    { value: 'exists', label: 'Заполнено' },
    { value: 'not_exists', label: 'Не заполнено' },
  ],
  number: [
    { value: 'equals', label: '=' },
    { value: 'gt', label: '>' },
    { value: 'gte', label: '≥' },
    { value: 'lt', label: '<' },
    { value: 'lte', label: '≤' },
    { value: 'exists', label: 'Заполнено' },
  ],
  date: [
    { value: 'equals', label: 'Равно' },
    { value: 'gte', label: 'От' },
    { value: 'lte', label: 'До' },
  ],
  boolean: [
    { value: 'is_true', label: 'Да' },
    { value: 'is_false', label: 'Нет' },
  ],
  select: [
    { value: 'equals', label: 'Равно' },
  ],
};

export default function UniversalSearch() {
  const [loading, setLoading] = useState(false);
  const [collections, setCollections] = useState({});
  const [selectedCollection, setSelectedCollection] = useState('trips');
  const [filters, setFilters] = useState([]);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState(-1);
  const LIMIT = 50;

  // Fetch collections info
  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const response = await axios.get(`${API}/api/search/collections`, getAuthHeaders());
        setCollections(response.data);
      } catch (error) {
        console.error('Error fetching collections:', error);
        toast.error('Ошибка загрузки коллекций');
      }
    };
    fetchCollections();
  }, []);

  // Get fields for selected collection
  const currentFields = collections[selectedCollection]?.fields || [];

  // Get important fields for table display
  const getDisplayFields = () => {
    if (selectedCollection === 'trips') {
      // Show most important trip fields
      return currentFields.filter(f => 
        ['date', 'route_name', 'driver_name', 'bus_number', 'payment_amount', 'driver_salary'].includes(f.name)
      );
    }
    return currentFields.slice(0, 7);
  };

  // Add new filter
  const addFilter = () => {
    const firstField = currentFields[0];
    setFilters([...filters, {
      id: Date.now(),
      field: firstField?.name || '',
      operator: 'contains',
      value: ''
    }]);
  };

  // Remove filter
  const removeFilter = (id) => {
    setFilters(filters.filter(f => f.id !== id));
  };

  // Update filter
  const updateFilter = (id, key, value) => {
    setFilters(filters.map(f => {
      if (f.id === id) {
        const updated = { ...f, [key]: value };
        // Reset operator when field changes
        if (key === 'field') {
          const fieldInfo = currentFields.find(cf => cf.name === value);
          const fieldType = fieldInfo?.type || 'text';
          updated.operator = OPERATORS[fieldType]?.[0]?.value || 'contains';
          updated.value = '';
        }
        return updated;
      }
      return f;
    }));
  };

  // Get field info
  const getFieldInfo = (fieldName) => {
    return currentFields.find(f => f.name === fieldName);
  };

  // Search
  const handleSearch = async (newPage = 0) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/search`, {
        collection: selectedCollection,
        filters: filters.filter(f => f.field && (f.value !== '' || ['is_true', 'is_false', 'exists', 'not_exists'].includes(f.operator))),
        limit: LIMIT,
        skip: newPage * LIMIT,
        sort_field: sortField,
        sort_order: sortOrder
      }, getAuthHeaders());
      
      setResults(response.data.results);
      setTotal(response.data.total);
      setPage(newPage);
      setExpandedRows(new Set());
    } catch (error) {
      console.error('Error searching:', error);
      toast.error('Ошибка поиска');
    } finally {
      setLoading(false);
    }
  };

  // Reset
  const handleReset = () => {
    setFilters([]);
    setResults([]);
    setTotal(0);
    setPage(0);
  };

  // Toggle row expansion
  const toggleRow = (index) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  // Export to CSV
  const exportToCSV = () => {
    if (results.length === 0) return;
    
    const headers = Object.keys(results[0]).join(',');
    const rows = results.map(r => 
      Object.values(r).map(v => 
        typeof v === 'string' ? `"${v.replace(/"/g, '""')}"` : v
      ).join(',')
    );
    
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${selectedCollection}_export.csv`;
    link.click();
  };

  // Render filter value input
  const renderFilterInput = (filter) => {
    const fieldInfo = getFieldInfo(filter.field);
    const fieldType = fieldInfo?.type || 'text';

    if (['is_true', 'is_false', 'exists', 'not_exists'].includes(filter.operator)) {
      return null; // No value input needed
    }

    if (fieldType === 'select' && fieldInfo?.options) {
      return (
        <Select
          value={filter.value}
          onValueChange={(val) => updateFilter(filter.id, 'value', val)}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Выберите..." />
          </SelectTrigger>
          <SelectContent>
            {fieldInfo.options.map(opt => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    if (fieldType === 'date') {
      return (
        <Input
          type="date"
          value={filter.value}
          onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
          className="w-40"
        />
      );
    }

    if (fieldType === 'number') {
      return (
        <Input
          type="number"
          value={filter.value}
          onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
          placeholder="Значение"
          className="w-32"
        />
      );
    }

    return (
      <Input
        type="text"
        value={filter.value}
        onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
        placeholder="Значение"
        className="w-48"
      />
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <CardHeader className="py-4">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Search className="w-5 h-5" />
            Универсальный поиск
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Collection selector */}
          <div className="flex items-center gap-4">
            <Label className="flex items-center gap-2">
              <Database className="w-4 h-4" />
              Коллекция:
            </Label>
            <Select
              value={selectedCollection}
              onValueChange={(val) => {
                setSelectedCollection(val);
                setFilters([]);
                setResults([]);
                setTotal(0);
              }}
            >
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(collections).map(([key, col]) => (
                  <SelectItem key={key} value={key}>{col.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Filters */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Фильтры ({filters.length})
              </Label>
              <Button
                variant="outline"
                size="sm"
                onClick={addFilter}
                className="gap-1"
              >
                <Plus className="w-4 h-4" />
                Добавить фильтр
              </Button>
            </div>

            {filters.length === 0 ? (
              <p className="text-sm text-gray-500 py-2">
                Нажмите «Добавить фильтр» для настройки условий поиска
              </p>
            ) : (
              <div className="space-y-2">
                {filters.map((filter, idx) => (
                  <div key={filter.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-500 w-6">{idx + 1}.</span>
                    
                    {/* Field selector */}
                    <Select
                      value={filter.field}
                      onValueChange={(val) => updateFilter(filter.id, 'field', val)}
                    >
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Поле" />
                      </SelectTrigger>
                      <SelectContent>
                        {currentFields.map(f => (
                          <SelectItem key={f.name} value={f.name}>{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {/* Operator selector */}
                    <Select
                      value={filter.operator}
                      onValueChange={(val) => updateFilter(filter.id, 'operator', val)}
                    >
                      <SelectTrigger className="w-36">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {OPERATORS[getFieldInfo(filter.field)?.type || 'text']?.map(op => (
                          <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {/* Value input */}
                    {renderFilterInput(filter)}

                    {/* Remove button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFilter(filter.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Search buttons */}
          <div className="flex items-center gap-2 pt-2 border-t">
            <Button
              onClick={() => handleSearch(0)}
              disabled={loading}
              className="gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Искать
            </Button>
            <Button
              variant="outline"
              onClick={handleReset}
              className="gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Сбросить
            </Button>
            {results.length > 0 && (
              <Button
                variant="outline"
                onClick={exportToCSV}
                className="gap-2 ml-auto"
              >
                <Download className="w-4 h-4" />
                Экспорт CSV
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {results.length > 0 && (
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-base flex items-center justify-between">
              <span>Результаты: {total} записей</span>
              <span className="text-sm font-normal text-gray-500">
                Стр. {page + 1} из {Math.ceil(total / LIMIT)}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-left w-8">#</th>
                    {getDisplayFields().map(f => (
                      <th 
                        key={f.name} 
                        className="px-3 py-2 text-left cursor-pointer hover:bg-gray-200"
                        onClick={() => {
                          if (sortField === f.name) {
                            setSortOrder(sortOrder === 1 ? -1 : 1);
                          } else {
                            setSortField(f.name);
                            setSortOrder(-1);
                          }
                        }}
                      >
                        <div className="flex items-center gap-1">
                          {f.label}
                          {sortField === f.name && (
                            sortOrder === 1 ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                          )}
                        </div>
                      </th>
                    ))}
                    <th className="px-3 py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((row, idx) => (
                    <React.Fragment key={idx}>
                      <tr 
                        className={`border-t hover:bg-blue-50 cursor-pointer ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                        onClick={() => toggleRow(idx)}
                      >
                        <td className="px-3 py-2 text-gray-500">{page * LIMIT + idx + 1}</td>
                        {getDisplayFields().map(f => (
                          <td key={f.name} className="px-3 py-2 max-w-[200px] truncate">
                            {typeof row[f.name] === 'boolean' 
                              ? (row[f.name] ? '✓' : '✗')
                              : (row[f.name] ?? '—')}
                          </td>
                        ))}
                        <td className="px-3 py-2">
                          {expandedRows.has(idx) ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </td>
                      </tr>
                      {expandedRows.has(idx) && (
                        <tr className="bg-blue-50">
                          <td colSpan={getDisplayFields().length + 2} className="px-3 py-3">
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 text-xs">
                              {Object.entries(row).map(([key, value]) => (
                                <div key={key} className="p-2 bg-white rounded border">
                                  <span className="font-medium text-gray-600">{key}:</span>
                                  <span className="ml-1 text-gray-900 break-all">
                                    {typeof value === 'boolean' ? (value ? 'Да' : 'Нет') : 
                                     typeof value === 'object' ? JSON.stringify(value) : 
                                     (value ?? '—')}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {total > LIMIT && (
              <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleSearch(page - 1)}
                  disabled={page === 0 || loading}
                >
                  Назад
                </Button>
                <span className="text-sm text-gray-500">
                  {page + 1} / {Math.ceil(total / LIMIT)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleSearch(page + 1)}
                  disabled={page >= Math.ceil(total / LIMIT) - 1 || loading}
                >
                  Вперёд
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {!loading && results.length === 0 && filters.length > 0 && (
        <Card>
          <CardContent className="py-8 text-center text-gray-500">
            <Search className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Нажмите «Искать» для поиска по заданным фильтрам</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
